/**
 * Date utilities specific to booking functionality
 */

/**
 * Parse a UTC date string (YYYY-MM-DD) and return a local Date object
 * This ensures consistent date handling across timezones
 */
export function parseUtcDateString(utcDateString: string): Date {
  // Parse as UTC date to avoid timezone interpretation issues
  const parts = utcDateString.split('-');
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
}

/**
 * Get the end datetime of a booking, accounting for bookings that cross midnight
 * @param dateString - The booking date in YYYY-MM-DD format
 * @param timeString - The booking time range (e.g., "21:00 - 00:00" or "14:00 - 16:00")
 * @returns Date object representing the end of the booking in local time
 */
export function getBookingEndDateTime(dateString: string, timeString?: string): Date {
  const date = parseUtcDateString(dateString);

  if (!timeString) {
    // No time specified, treat as end of day
    date.setHours(23, 59, 59, 999);
    return date;
  }

  // Extract end time from the time range (format: "HH:MM - HH:MM")
  const timeMatch = timeString.match(/-\s*(\d{1,2}):(\d{2})/);
  if (!timeMatch) {
    // Invalid time format, treat as end of day
    date.setHours(23, 59, 59, 999);
    return date;
  }

  const endHour = parseInt(timeMatch[1]);
  const endMinute = parseInt(timeMatch[2]);

  // Extract start time to detect midnight crossing
  const startTimeMatch = timeString.match(/(\d{1,2}):(\d{2})/);
  const startHour = startTimeMatch ? parseInt(startTimeMatch[1]) : 0;

  // Set the end time on the date
  date.setHours(endHour, endMinute, 0, 0);

  // If end time is earlier than start time, the booking crosses midnight to the next day
  // Example: "21:00 - 00:00" means 9 PM to midnight of the NEXT day
  // Example: "23:00 - 02:00" means 11 PM to 2 AM of the NEXT day
  const crossesMidnight = endHour < startHour || (endHour === 0 && endMinute === 0 && startHour > 0);
  if (crossesMidnight) {
    date.setDate(date.getDate() + 1);
  }

  return date;
}

/**
 * Get the start datetime of a booking
 * @param dateString - The booking date in YYYY-MM-DD format
 * @param timeString - The booking time range (e.g., "21:00 - 00:00" or "14:00 - 16:00")
 * @returns Date object representing the start of the booking in local time
 */
export function getBookingStartDateTime(dateString: string, timeString?: string): Date {
  const date = parseUtcDateString(dateString);

  if (!timeString) {
    // No time specified, treat as start of day
    date.setHours(0, 0, 0, 0);
    return date;
  }

  // Extract start time from the time range (format: "HH:MM - HH:MM")
  const startTimeMatch = timeString.match(/^(\d{1,2}):(\d{2})/);
  if (!startTimeMatch) {
    // Invalid time format, treat as start of day
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const startHour = parseInt(startTimeMatch[1]);
  const startMinute = parseInt(startTimeMatch[2]);

  date.setHours(startHour, startMinute, 0, 0);

  return date;
}

/**
 * Check if a booking has already started (current time is past the start time)
 * @param dateString - The booking date in YYYY-MM-DD format
 * @param timeString - The booking time range (optional)
 * @returns true if the booking has started
 */
export function hasBookingStarted(dateString: string, timeString?: string): boolean {
  const startDateTime = getBookingStartDateTime(dateString, timeString);
  return startDateTime.getTime() <= Date.now();
}

/**
 * Check if a booking is in the past based on its end datetime
 * @param dateString - The booking date in YYYY-MM-DD format
 * @param timeString - The booking time range (optional)
 * @returns true if the booking has ended
 */
export function isBookingPast(dateString: string, timeString?: string): boolean {
  const endDateTime = getBookingEndDateTime(dateString, timeString);
  return endDateTime.getTime() < Date.now();
}

/**
 * Check if a booking is upcoming based on its end datetime
 * @param dateString - The booking date in YYYY-MM-DD format
 * @param timeString - The booking time range (optional)
 * @returns true if the booking hasn't ended yet
 */
export function isBookingUpcoming(dateString: string, timeString?: string): boolean {
  return !isBookingPast(dateString, timeString);
}

/**
 * Check if a booking is currently ongoing (started but not yet ended)
 * @param dateString - The booking date in YYYY-MM-DD format
 * @param timeString - The booking time range (optional)
 * @returns true if the booking is currently in progress
 */
export function isBookingOngoing(dateString: string, timeString?: string): boolean {
  return hasBookingStarted(dateString, timeString) && !isBookingPast(dateString, timeString);
}

export interface GetTimeAgoOptions {
  /**
   * Whether to use short format for hours and minutes only (e.g., "2 hr" instead of "2 hours", "5 min" instead of "5 minutes")
   * Days, weeks, months, and years remain in full format
   * @default false
   */
  shortFormat?: boolean;
  /**
   * Translation function for time ago strings
   * Should be from useTranslations('Common.timeAgo')
   */
  t?: (key: string, values?: Record<string, any>) => string;
}

export function getTimeAgo(dateString: string, timeString?: string, options?: GetTimeAgoOptions): string {
  const { shortFormat = false, t } = options || {};

  // Check if booking is currently ongoing (started but not ended)
  const ongoing = isBookingOngoing(dateString, timeString);
  if (ongoing) {
    return t ? t('now') : 'now';
  }

  // Determine which datetime to use:
  // - For bookings that have ended: use END time (show "X ago")
  // - For bookings not yet started: use START time (show "in X")
  const bookingEnded = isBookingPast(dateString, timeString);
  const date = bookingEnded
    ? getBookingEndDateTime(dateString, timeString)
    : getBookingStartDateTime(dateString, timeString);

  const now = new Date();
  const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);

  const isUpcoming = diffInSeconds > 0;
  const absDiffInSeconds = Math.abs(diffInSeconds);

  if (absDiffInSeconds < 60) {
    return isUpcoming ? (t ? t('startingSoon') : 'starting soon') : t ? t('justNow') : 'just now';
  }

  const diffInMinutes = Math.floor(absDiffInSeconds / 60);
  if (diffInMinutes < 60) {
    if (shortFormat) {
      if (isUpcoming) {
        return diffInMinutes === 1
          ? t
            ? t('inMin')
            : 'in 1 min'
          : t
            ? t('inMins', { count: diffInMinutes })
            : `in ${diffInMinutes} min`;
      }
      return diffInMinutes === 1
        ? t
          ? t('minAgo')
          : '1 min ago'
        : t
          ? t('minsAgo', { count: diffInMinutes })
          : `${diffInMinutes} min ago`;
    }
    if (isUpcoming) {
      return diffInMinutes === 1
        ? t
          ? t('inMinute')
          : 'in 1 minute'
        : t
          ? t('inMinutes', { count: diffInMinutes })
          : `in ${diffInMinutes} minutes`;
    }
    return diffInMinutes === 1
      ? t
        ? t('minuteAgo')
        : '1 minute ago'
      : t
        ? t('minutesAgo', { count: diffInMinutes })
        : `${diffInMinutes} minutes ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    if (shortFormat) {
      if (isUpcoming) {
        return diffInHours === 1
          ? t
            ? t('inHr')
            : 'in 1 hr'
          : t
            ? t('inHrs', { count: diffInHours })
            : `in ${diffInHours} hr`;
      }
      return diffInHours === 1
        ? t
          ? t('hrAgo')
          : '1 hr ago'
        : t
          ? t('hrsAgo', { count: diffInHours })
          : `${diffInHours} hr ago`;
    }
    if (isUpcoming) {
      return diffInHours === 1
        ? t
          ? t('inHour')
          : 'in 1 hour'
        : t
          ? t('inHours', { count: diffInHours })
          : `in ${diffInHours} hours`;
    }
    return diffInHours === 1
      ? t
        ? t('hourAgo')
        : '1 hour ago'
      : t
        ? t('hoursAgo', { count: diffInHours })
        : `${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    if (shortFormat) {
      if (isUpcoming) {
        return diffInDays === 1 ? (t ? t('inD') : '1 d') : t ? t('inDs', { count: diffInDays }) : `${diffInDays} d`;
      }
      return diffInDays === 1 ? (t ? t('dAgo') : '-1 d') : t ? t('dsAgo', { count: diffInDays }) : `-${diffInDays} d`;
    }
    if (isUpcoming) {
      return diffInDays === 1
        ? t
          ? t('inDay')
          : 'in 1 day'
        : t
          ? t('inDays', { count: diffInDays })
          : `in ${diffInDays} days`;
    }
    return diffInDays === 1
      ? t
        ? t('dayAgo')
        : '1 day ago'
      : t
        ? t('daysAgo', { count: diffInDays })
        : `${diffInDays} days ago`;
  }

  // Less than 30 days: show weeks
  if (diffInDays < 30) {
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (shortFormat) {
      if (isUpcoming) {
        return diffInWeeks === 1
          ? t
            ? t('inW')
            : '1 wk'
          : t
            ? t('inWs', { count: diffInWeeks })
            : `${diffInWeeks} wk`;
      }
      return diffInWeeks === 1
        ? t
          ? t('wAgo')
          : '-1 wk'
        : t
          ? t('wsAgo', { count: diffInWeeks })
          : `-${diffInWeeks} wk`;
    }
    if (isUpcoming) {
      return diffInWeeks === 1
        ? t
          ? t('inWeek')
          : 'in 1 week'
        : t
          ? t('inWeeks', { count: diffInWeeks })
          : `in ${diffInWeeks} weeks`;
    }
    return diffInWeeks === 1
      ? t
        ? t('weekAgo')
        : '1 week ago'
      : t
        ? t('weeksAgo', { count: diffInWeeks })
        : `${diffInWeeks} weeks ago`;
  }

  // Less than 365 days: show months
  if (diffInDays < 365) {
    const diffInMonths = Math.floor(diffInDays / 30);
    if (shortFormat) {
      if (isUpcoming) {
        return diffInMonths === 1
          ? t
            ? t('inMo')
            : '1 mo'
          : t
            ? t('inMos', { count: diffInMonths })
            : `${diffInMonths} mo`;
      }
      return diffInMonths === 1
        ? t
          ? t('moAgo')
          : '-1 mo'
        : t
          ? t('mosAgo', { count: diffInMonths })
          : `-${diffInMonths} mo`;
    }
    if (isUpcoming) {
      return diffInMonths === 1
        ? t
          ? t('inMonth')
          : 'in 1 month'
        : t
          ? t('inMonths', { count: diffInMonths })
          : `in ${diffInMonths} months`;
    }
    return diffInMonths === 1
      ? t
        ? t('monthAgo')
        : '1 month ago'
      : t
        ? t('monthsAgo', { count: diffInMonths })
        : `${diffInMonths} months ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  if (shortFormat) {
    if (isUpcoming) {
      return diffInYears === 1
        ? t
          ? t('inYr')
          : '1 yr'
        : t
          ? t('inYrs', { count: diffInYears })
          : `${diffInYears} yr`;
    }
    return diffInYears === 1
      ? t
        ? t('yrAgo')
        : '-1 yr'
      : t
        ? t('yrsAgo', { count: diffInYears })
        : `-${diffInYears} yr`;
  }
  if (isUpcoming) {
    return diffInYears === 1
      ? t
        ? t('inYear')
        : 'in 1 year'
      : t
        ? t('inYears', { count: diffInYears })
        : `in ${diffInYears} years`;
  }
  return diffInYears === 1
    ? t
      ? t('yearAgo')
      : '1 year ago'
    : t
      ? t('yearsAgo', { count: diffInYears })
      : `${diffInYears} years ago`;
}
